import { useCallback, useEffect, useState } from "react";
import useEscape from "../../hooks/useEscape";
import { Search, X } from "lucide-react";
import { apiFetch } from "../../data/session";
import { Waitlist } from "./Waitlist";

const ROLE = {
  parent: "Parent",
  student: "Élève",
  tutor: "Tuteur",
  teacher: "Enseignant",
  admin: "Admin",
};
const STATUS = {
  active: ["Actif", "bg-[#E9FBF0] text-[#128a3e]"],
  pending: ["Invité", "bg-[#FFF8E1] text-[#8a6500]"],
  waiting: ["En attente", "bg-[#FFF8E1] text-[#8a6500]"],
  expired: ["Expiré", "bg-gray-100 text-gray-600"],
  revoked: ["Révoqué", "bg-gray-100 text-gray-600"],
  deactivated: ["Désactivé", "bg-[#FDF2F1] text-[#c0392b]"],
  removed: ["Supprimé", "bg-[#FDF2F1] text-[#c0392b]"],
};
const PLAN_STATUS = {
  pending_acceptance: "En attente d’accord",
  change_requested: "Changement demandé",
  active: "Actifs",
  completed: "Terminés",
  declined: "Refusés",
  cancelled: "Annulés",
  expired: "Expirés",
};
const FILTERS = [
  ["all", "Tous"],
  ["parent", "Parents"],
  ["student", "Élèves"],
  ["tutor", "Tuteurs"],
  ["teacher", "Enseignants"],
  ["admin", "Admins"],
];

function when(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("fr-CA", { dateStyle: "medium", timeStyle: "short" });
}

const Badge = ({ children, className = "" }) => (
  <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider ${className}`}>
    {children}
  </span>
);

const RoleBadge = ({ role }) => (
  <Badge className={role === "admin" ? "bg-[#0a2914] text-[#25D366]" : "bg-[#EEF5F1] text-[#2d5a3d]"}>
    {ROLE[role] || role}
  </Badge>
);

const StatusBadge = ({ status }) => {
  const [label, cls] = STATUS[status] || [status, "bg-gray-100 text-gray-600"];
  return <Badge className={cls}>{label}</Badge>;
};

const btn = "whitespace-nowrap rounded-full border px-3 py-1.5 text-[13px] font-semibold transition-colors disabled:opacity-50";
const btnGhost = `${btn} border-gray-200 text-[#122118] hover:border-[#25D366] hover:bg-[#F3FCF7]`;
const btnDanger = `${btn} border-[#f1c7c2] text-[#c0392b] hover:bg-[#FDF2F1]`;

// ---------- edit dialog ----------

const ForfaitSelect = ({ user, token, onSaved, testPrefix = "admin-forfait" }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  if (user.role === "admin") return <span className="text-xs text-[#829C8D]">—</span>;
  const change = async (event) => {
    setBusy(true);
    setError("");
    try {
      const data = await apiFetch(`/api/admin/users/${user.id}`, token, {
        method: "PATCH",
        body: JSON.stringify({ forfait: Number(event.target.value) }),
      });
      onSaved(data.user);
    } catch (problem) {
      setError(problem.message || "Modification impossible.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className="inline-flex flex-col">
    <select
      value={user.forfait || 1}
      onChange={change}
      disabled={busy}
      aria-label={`Forfait de ${user.name || user.email}`}
      className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs font-semibold text-[#122118]"
      data-testid={`${testPrefix}-${user.id}`}
    >
      <option value={1}>1 · Essentiel</option>
      <option value={2}>2 · Famille</option>
      <option value={3}>3 · Famille Plus</option>
    </select>
    {error && <span className="mt-1 text-[10px] text-[#9b3b3b]" data-testid={`${testPrefix}-error-${user.id}`}>{error}</span>}
    </span>
  );
};

const IntegratorBox = ({ user, token, onSaved, testPrefix = "admin-integrator" }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  if (user.role === "admin") return <span className="text-xs text-[#829C8D]">—</span>;
  const change = async (event) => {
    setBusy(true);
    setError("");
    try {
      const data = await apiFetch(`/api/admin/users/${user.id}`, token, {
        method: "PATCH",
        body: JSON.stringify({ integrator: event.target.checked }),
      });
      onSaved(data.user);
    } catch (problem) {
      setError(problem.message || "Modification impossible.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#122118]" title="Accès aux corrections et à la publication dans le simulateur">
      <input
        type="checkbox"
        checked={Boolean(user.integrator)}
        disabled={busy}
        onChange={change}
        aria-label={`Intégrateur : ${user.name || user.email}`}
        data-testid={`${testPrefix}-${user.id}`}
      />
      Intégrateur
      {error && <span className="text-[10px] text-[#9b3b3b]" data-testid={`${testPrefix}-error-${user.id}`}>{error}</span>}
    </label>
  );
};

const EditUserDialog = ({ user, token, selfId, onClose, onSaved }) => {
  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const [role, setRole] = useState(user.role);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [resetLink, setResetLink] = useState("");
  const swappable = user.role === "tutor" || user.role === "teacher";
  useEscape(onClose);
  const isSelf = user.id === selfId;

  const call = async (fn) => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await fn();
    } catch (problem) {
      setError(problem.message);
    } finally {
      setBusy(false);
    }
  };

  const save = (event) => {
    event.preventDefault();
    call(async () => {
      const body = {};
      if (name !== user.name) body.name = name;
      if (email !== user.email) body.email = email;
      if (swappable && role !== user.role) body.role = role;
      if (!Object.keys(body).length) return onClose();
      const data = await apiFetch(`/api/admin/users/${user.id}`, token, { method: "PATCH", body: JSON.stringify(body) });
      onSaved(data.user, "Modifications enregistrées.");
      onClose();
      return undefined;
    });
  };

  const reset = () =>
    call(async () => {
      const data = await apiFetch(`/api/admin/users/${user.id}/reset-password`, token, { method: "POST" });
      if (data.sent) setNotice(`Lien envoyé à ${user.email}. Il reste valide une heure.`);
      else if (data.resetPath) {
        setNotice("Mode local, aucun courriel envoyé. Transmettez ce lien (valide une heure) :");
        setResetLink(`${window.location.origin}${data.resetPath}`);
      }
      else setNotice("Le courriel n’a pas pu partir. Vérifiez la configuration d’envoi.");
    });

  const toggleActive = () =>
    call(async () => {
      const action = user.status === "deactivated" ? "reactivate" : "deactivate";
      const data = await apiFetch(`/api/admin/users/${user.id}/${action}`, token, { method: "POST" });
      onSaved(data.user, action === "deactivate" ? "Compte désactivé, sessions fermées." : "Compte réactivé.");
      onClose();
    });

  const field = "mt-1.5 w-full rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm outline-none focus:border-[#25D366]";
  const label = "mt-4 block text-[11px] font-bold uppercase tracking-wider text-[#189346]";
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-[#0E1A14]/55 p-4" data-testid="admin-edit-dialog">
      <form onSubmit={save} className="relative max-h-[92vh] w-full max-w-md overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl">
        <button type="button" aria-label="Fermer" onClick={onClose} className="absolute right-4 top-4 rounded-full p-1.5 text-[#4a5f53] hover:bg-[#F3FCF7]">
          <X className="h-5 w-5" />
        </button>
        <h3 className="font-display text-xl font-bold text-[#122118]">Modifier l’utilisateur</h3>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <RoleBadge role={user.role} />
          <StatusBadge status={user.status} />
          {user.familyName && <span className="text-xs text-[#4a5f53]">{user.familyName}</span>}
        </div>
        <label className={label} htmlFor="admin-edit-name">Nom complet</label>
        <input id="admin-edit-name" data-testid="admin-edit-name" value={name} onChange={(e) => setName(e.target.value)} className={field} />
        <label className={label} htmlFor="admin-edit-email">Courriel</label>
        <input id="admin-edit-email" data-testid="admin-edit-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={field} />
        <label className={label} htmlFor="admin-edit-role">Rôle</label>
        {swappable ? (
          <select id="admin-edit-role" data-testid="admin-edit-role" value={role} onChange={(e) => setRole(e.target.value)} className={field}>
            <option value="tutor">Tuteur</option>
            <option value="teacher">Enseignant</option>
          </select>
        ) : (
          <p className="mt-1.5 text-sm text-[#4a5f53]">
            {ROLE[user.role]} — rôle lié à la famille, non modifiable ici.
          </p>
        )}
        <div className="mt-6 rounded-2xl bg-[#F7FAF8] p-4">
          <p className="text-[11px] font-bold uppercase tracking-wider text-[#829C8D]">Accès</p>
          <p className="mt-1 text-xs text-[#4a5f53]">
            Dernière connexion : {when(user.lastLoginAt)} · Mot de passe {user.hasPassword ? "choisi" : "pas encore choisi"}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" onClick={reset} disabled={busy || user.status !== "active"} className={btnGhost} data-testid="admin-reset-password">
              Réinitialiser le mot de passe
            </button>
            {!isSelf && (user.status === "active" || user.status === "deactivated") && (
              <button type="button" onClick={toggleActive} disabled={busy} className={user.status === "deactivated" ? btnGhost : btnDanger} data-testid="admin-toggle-active">
                {user.status === "deactivated" ? "Réactiver" : "Désactiver"}
              </button>
            )}
          </div>
        </div>
        {notice && (
          <div className="mt-3 text-sm text-[#2d5a3d]" data-testid="admin-edit-notice">
            <p>{notice}</p>
            {resetLink && (
              <div className="mt-2 flex items-center gap-2">
                <a href={resetLink} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate text-[#128a3e] underline" data-testid="admin-reset-link">
                  {resetLink}
                </a>
                <button
                  type="button"
                  className={btnGhost}
                  onClick={() => navigator.clipboard?.writeText(resetLink).then(() => setNotice("Lien copié."))}
                  data-testid="admin-reset-copy"
                >
                  Copier
                </button>
              </div>
            )}
          </div>
        )}
        {error && <p className="mt-3 text-sm text-[#9b3b3b]" data-testid="admin-edit-error">{error}</p>}
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-full px-5 py-2.5 text-sm font-semibold text-[#4a5f53]">Annuler</button>
          <button type="submit" disabled={busy} data-testid="admin-edit-save" className="rounded-full bg-[#25D366] px-5 py-2.5 text-sm font-semibold text-[#0a2914] disabled:opacity-50">
            Enregistrer
          </button>
        </div>
      </form>
    </div>
  );
};

const ConfirmRemove = ({ user, token, onClose, onRemoved }) => {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEscape(onClose);
  const remove = async () => {
    setBusy(true);
    setError("");
    try {
      await apiFetch(`/api/admin/users/${user.id}`, token, { method: "DELETE" });
      onRemoved(user);
      onClose();
    } catch (problem) {
      setError(problem.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-[#0E1A14]/55 p-4" data-testid="admin-remove-dialog">
      <div className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-2xl">
        <h3 className="font-display text-xl font-bold text-[#122118]">Supprimer {user.name || user.email} ?</h3>
        <p className="mt-2 text-sm leading-relaxed text-[#4a5f53]">
          Le compte est fermé et ses sessions sont coupées. Ses données (engagements, réalisations) restent conservées.
        </p>
        {error && <p className="mt-3 text-sm text-[#9b3b3b]" data-testid="admin-remove-error">{error}</p>}
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-full px-5 py-2.5 text-sm font-semibold text-[#4a5f53]">Annuler</button>
          <button type="button" onClick={remove} disabled={busy} data-testid="admin-remove-confirm" className="rounded-full bg-[#c0392b] px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50">
            Supprimer
          </button>
        </div>
      </div>
    </div>
  );
};

const ConfirmPurge = ({ users, token, onClose, onDone }) => {
  const [typed, setTyped] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEscape(onClose);
  const purge = async () => {
    setBusy(true);
    setError("");
    try {
      const data = await apiFetch("/api/admin/users/purge", token, {
        method: "POST",
        body: JSON.stringify({ ids: users.map((user) => user.id) }),
      });
      onDone(data);
      onClose();
    } catch (problem) {
      setError(problem.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-[#0E1A14]/55 p-4" data-testid="admin-purge-dialog">
      <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl">
        <h3 className="font-display text-xl font-bold text-[#122118]">
          Supprimer définitivement {users.length} compte{users.length > 1 ? "s" : ""} ?
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-[#4a5f53]">
          Les comptes, leurs engagements, réalisations et photos sont effacés. On ne peut pas revenir en arrière.
          Un parent dont la famille garde des membres non sélectionnés est ignoré. Les invitations expirées ou révoquées de sa famille sont aussi effacées.
        </p>
        <ul className="mt-3 max-h-40 overflow-y-auto rounded-xl bg-[#F7FAF8] px-3 py-2 text-xs text-[#4a5f53]" data-testid="admin-purge-list">
          {users.map((user) => (
            <li key={user.id} className="truncate py-0.5">{user.name || "—"} · {user.email}</li>
          ))}
        </ul>
        <label className="mt-4 block text-[11px] font-bold uppercase tracking-wider text-[#c0392b]" htmlFor="admin-purge-typed">
          Écrivez SUPPRIMER pour confirmer
        </label>
        <input
          id="admin-purge-typed"
          data-testid="admin-purge-typed"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          className="mt-1.5 w-full rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm outline-none focus:border-[#c0392b]"
        />
        {error && <p className="mt-3 text-sm text-[#9b3b3b]" data-testid="admin-purge-error">{error}</p>}
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-full px-5 py-2.5 text-sm font-semibold text-[#4a5f53]">Annuler</button>
          <button
            type="button"
            onClick={purge}
            disabled={busy || typed.trim() !== "SUPPRIMER"}
            data-testid="admin-purge-confirm"
            className="rounded-full bg-[#c0392b] px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
          >
            {busy ? "Suppression…" : "Supprimer définitivement"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------- tabs ----------

const Users = ({ token, selfId }) => {
  const [query, setQuery] = useState("");
  const [applied, setApplied] = useState("");
  const [role, setRole] = useState("all");
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [picked, setPicked] = useState([]);
  const [purging, setPurging] = useState(false);
  const selectable = users.filter((user) => user.role !== "admin" && user.id !== selfId);
  const allPicked = selectable.length > 0 && selectable.every((user) => picked.includes(user.id));
  const toggle = (id) => setPicked((ids) => (ids.includes(id) ? ids.filter((item) => item !== id) : [...ids, id]));
  const toggleAll = () => setPicked(allPicked ? [] : selectable.map((user) => user.id));
  const pickBox = (user) =>
    user.role === "admin" || user.id === selfId ? (
      <span className="inline-block h-4 w-4" />
    ) : (
      <input
        type="checkbox"
        aria-label={`Sélectionner ${user.name || user.email}`}
        checked={picked.includes(user.id)}
        onChange={() => toggle(user.id)}
        className="h-4 w-4 align-middle accent-[#c0392b]"
        data-testid={`admin-pick-${user.id}`}
      />
    );
  const afterPurge = ({ removed, skipped }) => {
    setUsers((rows) => rows.filter((row) => !removed.includes(row.id)));
    setTotal((count) => Math.max(0, count - removed.length));
    setPicked([]);
    const parts = [`${removed.length} compte${removed.length > 1 ? "s" : ""} supprimé${removed.length > 1 ? "s" : ""} définitivement.`];
    if (skipped.length) parts.push(`Ignorés : ${skipped.map((item) => `${item.name} (${item.reason})`).join(", ")}`);
    setNotice(parts.join(" "));
  };

  useEffect(() => setPicked([]), [applied, role]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ q: applied, role });
      const data = await apiFetch(`/api/admin/users?${params}`, token);
      setUsers(data.users);
      setTotal(data.total || data.users.length);
    } catch (problem) {
      setError(problem.message);
    } finally {
      setLoading(false);
    }
  }, [applied, role, token]);

  useEffect(() => {
    load();
  }, [load]);

  const replace = (user, message) => {
    setUsers((rows) => rows.map((row) => (row.id === user.id ? { ...row, ...user } : row)));
    setNotice(message);
  };

  const actions = (user) =>
    user.status === "removed" ? (
      <span className="text-xs text-[#829C8D]">Supprimé</span>
    ) : (
      <div className="flex justify-end gap-2">
        <button type="button" className={btnGhost} onClick={() => setEditing(user)} data-testid={`admin-edit-${user.id}`}>
          Modifier
        </button>
        {user.id !== selfId && (
          <button type="button" className={btnDanger} onClick={() => setRemoving(user)} data-testid={`admin-remove-${user.id}`}>
            Supprimer
          </button>
        )}
      </div>
    );

  return (
    <section className="rounded-3xl border border-gray-100 bg-white p-5 shadow-[0_10px_40px_-20px_rgba(10,41,20,0.25)] sm:p-7" data-testid="admin-users">
      <h2 className="font-display text-xl font-bold text-[#122118]">Gestion des utilisateurs</h2>
      <p className="mt-1 text-sm text-[#4a5f53]">
        Modifier les comptes, réinitialiser les mots de passe, désactiver ou supprimer. Visible uniquement par les administrateurs.
      </p>
      <form
        className="mt-5 flex gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          setApplied(query.trim());
        }}
      >
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#829C8D]" />
          <input
            data-testid="admin-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Nom, courriel, famille…"
            className="w-full rounded-2xl border border-gray-200 bg-[#F7FAF8] py-3 pl-10 pr-4 text-sm outline-none focus:border-[#25D366] focus:bg-white"
          />
        </div>
        <button
          type="submit"
          data-testid="admin-search-submit"
          aria-label="Rechercher"
          className="flex shrink-0 items-center justify-center rounded-2xl border border-gray-200 px-3.5 text-sm font-semibold text-[#122118] hover:border-[#25D366] sm:px-5"
        >
          <Search className="h-4 w-4 sm:hidden" />
          <span className="hidden sm:inline">Rechercher</span>
        </button>
      </form>
      <div className="mt-3 flex flex-wrap gap-2" data-testid="admin-role-filters">
        {FILTERS.map(([id, label]) => (
          <button
            key={id}
            type="button"
            data-testid={`admin-filter-${id}`}
            onClick={() => setRole(id)}
            className={`rounded-full px-3.5 py-1.5 text-[13px] font-semibold ${
              role === id ? "bg-[#0a2914] text-white" : "bg-[#EEF5F1] text-[#2d5a3d] hover:bg-[#E2EEE7]"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {!loading && users.length > 0 && (
        <p className="mt-4 text-xs text-[#829C8D]" data-testid="admin-count">
          {users.length < total ? `${users.length} premiers sur ${total} — affinez la recherche.` : `${total} compte${total > 1 ? "s" : ""}`}
        </p>
      )}
      {notice && <p className="mt-4 rounded-xl bg-[#E9FBF0] px-4 py-2.5 text-sm text-[#128a3e]" data-testid="admin-notice">{notice}</p>}
      {error && <p className="mt-4 text-sm text-[#9b3b3b]">{error}</p>}
      {selectable.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-2xl border border-gray-100 bg-[#F7FAF8] px-4 py-2.5" data-testid="admin-batch-bar">
          <label className="flex items-center gap-2 text-sm font-semibold text-[#122118]">
            <input type="checkbox" checked={allPicked} onChange={toggleAll} className="h-4 w-4 accent-[#c0392b]" data-testid="admin-pick-all" />
            Tout sélectionner
          </label>
          <span className="text-xs text-[#829C8D]" data-testid="admin-picked-count">
            {picked.length} sélectionné{picked.length > 1 ? "s" : ""}
          </span>
          <button
            type="button"
            disabled={!picked.length}
            onClick={() => setPurging(true)}
            className={`${btnDanger} ml-auto`}
            data-testid="admin-purge-open"
          >
            Supprimer définitivement
          </button>
        </div>
      )}

      {/* desktop table */}
      <div className="mt-5 hidden overflow-x-auto md:block">
        <table className="w-full text-left text-sm" data-testid="admin-users-table">
          <thead>
            <tr className="border-b border-gray-100 text-[11px] font-bold uppercase tracking-wider text-[#829C8D]">
              <th className="w-8 py-3 pr-2"><span className="sr-only">Sélection</span></th>
              <th className="whitespace-nowrap py-3 pr-4">Nom complet</th>
              <th className="py-3 pr-4">Courriel</th>
              <th className="py-3 pr-4">Rôle</th>
              <th className="py-3 pr-4">Forfait</th>
              <th className="py-3 pr-4">Simulateur</th>
              <th className="py-3 pr-4">Famille</th>
              <th className="py-3 pr-4">Statut</th>
              <th className="whitespace-nowrap py-3 pr-4">Dernière connexion</th>
              <th className="py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} className="border-b border-gray-50 align-middle last:border-0" data-testid={`admin-row-${user.id}`}>
                <td className="py-3.5 pr-2">{pickBox(user)}</td>
                <td className="whitespace-nowrap py-3.5 pr-4 font-semibold text-[#122118]">{user.name || "—"}</td>
                <td className="max-w-[12rem] truncate py-3.5 pr-4 text-[#4a5f53]" title={user.email}>{user.email}</td>
                <td className="py-3.5 pr-4"><RoleBadge role={user.role} /></td>
                <td className="py-3.5 pr-4"><ForfaitSelect user={user} token={token} onSaved={replace} /></td>
                <td className="py-3.5 pr-4"><IntegratorBox user={user} token={token} onSaved={replace} /></td>
                <td className="whitespace-nowrap py-3.5 pr-4 text-[#4a5f53]">{user.familyName || "—"}</td>
                <td className="py-3.5 pr-4"><StatusBadge status={user.status} /></td>
                <td className="whitespace-nowrap py-3.5 pr-4 text-xs text-[#4a5f53]">{when(user.lastLoginAt)}</td>
                <td className="py-3.5">{actions(user)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* mobile cards */}
      <ul className="mt-5 space-y-3 md:hidden">
        {users.map((user) => (
          <li key={user.id} className="rounded-2xl border border-gray-100 p-4" data-testid={`admin-card-${user.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="mt-1 shrink-0">{pickBox(user)}</div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-[#122118]">{user.name || "—"}</p>
                <p className="truncate text-sm text-[#4a5f53]">{user.email}</p>
              </div>
              <StatusBadge status={user.status} />
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-[#4a5f53]">
              <RoleBadge role={user.role} />
              <ForfaitSelect user={user} token={token} onSaved={replace} testPrefix="admin-forfait-m" />
              <IntegratorBox user={user} token={token} onSaved={replace} testPrefix="admin-integrator-m" />
              {user.familyName && <span>{user.familyName}</span>}
            </div>
            <div className="mt-3">{actions(user)}</div>
          </li>
        ))}
      </ul>
      {!loading && !users.length && <p className="mt-6 text-center text-sm text-[#829C8D]">Aucun utilisateur trouvé.</p>}
      {loading && <p className="mt-6 text-center text-sm text-[#829C8D]">Chargement…</p>}

      {editing && <EditUserDialog user={editing} token={token} selfId={selfId} onClose={() => setEditing(null)} onSaved={replace} />}
      {removing && (
        <ConfirmRemove
          user={removing}
          token={token}
          onClose={() => setRemoving(null)}
          onRemoved={(user) => replace({ ...user, status: "removed" }, "Compte supprimé. Les données sont conservées.")}
        />
      )}
      {purging && (
        <ConfirmPurge
          users={users.filter((user) => picked.includes(user.id))}
          token={token}
          onClose={() => setPurging(false)}
          onDone={afterPurge}
        />
      )}
    </section>
  );
};

const Stat = ({ label, value }) => (
  <div className="rounded-2xl border border-gray-100 bg-white p-5">
    <p className="text-[11px] font-bold uppercase tracking-wider text-[#829C8D]">{label}</p>
    <p className="mt-2 font-display text-3xl font-bold text-[#122118]">{value}</p>
  </div>
);

const Overview = ({ token }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    apiFetch("/api/admin/overview", token).then(setData).catch((problem) => setError(problem.message));
  }, [token]);
  if (error) return <p className="text-sm text-[#9b3b3b]">{error}</p>;
  if (!data) return <p className="text-sm text-[#829C8D]">Chargement…</p>;
  const plans = Object.entries(data.plansByStatus || {});
  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Comptes" value={data.accounts} />
        <Stat label="Familles" value={data.families} />
        <Stat label="Élèves" value={data.byRole?.student || 0} />
        <Stat label="Réalisations" value={data.checkins} />
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <div className="rounded-2xl border border-gray-100 bg-white p-5">
          <p className="text-[11px] font-bold uppercase tracking-wider text-[#829C8D]">Comptes par statut</p>
          <ul className="mt-3 space-y-2">
            {Object.entries(data.byStatus || {}).map(([status, count]) => (
              <li key={status} className="flex items-center justify-between text-sm">
                <StatusBadge status={status} />
                <span className="font-semibold">{count}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-2xl border border-gray-100 bg-white p-5">
          <p className="text-[11px] font-bold uppercase tracking-wider text-[#829C8D]">Engagements</p>
          {plans.length ? (
            <ul className="mt-3 space-y-2">
              {plans.map(([status, count]) => (
                <li key={status} className="flex items-center justify-between text-sm">
                  <span className="text-[#4a5f53]">{PLAN_STATUS[status] || status}</span>
                  <span className="font-semibold">{count}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-3 text-sm text-[#829C8D]">Aucun engagement pour l’instant.</p>
          )}
        </div>
      </div>
    </div>
  );
};

const ACTION_LABEL = {
  "plan.create": "a créé un engagement",
  "plan.accept": "a accepté un engagement",
  "plan.request_change": "a demandé un changement",
  "plan.decline": "a refusé un engagement",
  "plan.cancel": "a annulé un engagement",
  "plan.edit": "a modifié un engagement",
  "admin.user.edit": "a modifié le compte de",
  "admin.user.reset_password": "a réinitialisé le mot de passe de",
  "admin.user.deactivate": "a désactivé",
  "admin.user.reactivate": "a réactivé",
  "admin.user.remove": "a supprimé",
  "admin.user.purge": "a supprimé définitivement",
};

const Journal = ({ token }) => {
  const [events, setEvents] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    apiFetch("/api/admin/audit", token).then((data) => setEvents(data.events)).catch((problem) => setError(problem.message));
  }, [token]);
  if (error) return <p className="text-sm text-[#9b3b3b]">{error}</p>;
  if (!events) return <p className="text-sm text-[#829C8D]">Chargement…</p>;
  return (
    <section className="rounded-3xl border border-gray-100 bg-white p-5 sm:p-7" data-testid="admin-journal">
      <h2 className="font-display text-xl font-bold text-[#122118]">Journal des actions</h2>
      {!events.length && <p className="mt-4 text-sm text-[#829C8D]">Aucune action enregistrée.</p>}
      <ul className="mt-4 divide-y divide-gray-50">
        {events.map((event) => (
          <li key={event.id} className="flex flex-col gap-0.5 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
            <span className="text-[#122118]">
              <span className="font-semibold">{event.actorName || event.actorRole}</span>{" "}
              {ACTION_LABEL[event.action] || event.action}
              {event.targetType === "account" && event.targetName ? <span className="font-semibold"> {event.targetName}</span> : null}
            </span>
            <span className="text-xs text-[#829C8D]">{when(event.at)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
};

const FeedbackImage = ({ id, token }) => {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let blobUrl = "";
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/feedback/images/${id}`, { headers: { "X-Account-Token": token } })
      .then((response) => (response.ok ? response.blob() : null))
      .then((blob) => {
        if (!blob) return;
        blobUrl = URL.createObjectURL(blob);
        setUrl(blobUrl);
      })
      .catch(() => {});
    return () => blobUrl && URL.revokeObjectURL(blobUrl);
  }, [id, token]);
  if (!url) return <span className="flex h-20 w-20 items-center justify-center rounded-xl bg-gray-50 text-[10px] text-[#829C8D]">Image…</span>;
  return (
    <a href={url} target="_blank" rel="noreferrer" data-testid={`admin-feedback-image-${id}`}>
      <img src={url} alt="Capture jointe" className="h-20 w-20 rounded-xl border border-gray-100 object-cover transition-transform hover:scale-105" />
    </a>
  );
};

const Feedback = ({ token }) => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    apiFetch("/api/admin/feedback", token).then((data) => setRows(data.feedback)).catch((problem) => setError(problem.message));
  }, [token]);
  if (error) return <p className="text-sm text-[#9b3b3b]">{error}</p>;
  if (!rows) return <p className="text-sm text-[#829C8D]">Chargement…</p>;
  return (
    <section className="rounded-3xl border border-gray-100 bg-white p-5 sm:p-7" data-testid="admin-feedback">
      <h2 className="font-display text-xl font-bold text-[#122118]">Commentaires</h2>
      {!rows.length && <p className="mt-4 text-sm text-[#829C8D]">Aucun commentaire pour l’instant.</p>}
      <ul className="mt-4 divide-y divide-gray-50">
        {rows.map((row) => (
          <li key={row.id} className="py-3 text-sm" data-testid={`admin-feedback-${row.id}`}>
            <p className="font-semibold text-[#122118]">
              {row.name || row.email} · {row.role}{row.rating ? ` · ${"★".repeat(row.rating)}` : ""}
              <span className="ml-2 text-xs font-normal text-[#829C8D]">{when(row.createdAt)} {row.context}</span>
            </p>
            {row.bug && <p className="mt-1 text-[#4a5f53]"><span className="font-semibold">Bug :</span> {row.bug}</p>}
            {row.suggestion && <p className="mt-1 text-[#4a5f53]"><span className="font-semibold">Suggestion :</span> {row.suggestion}</p>}
            {row.other && <p className="mt-1 text-[#4a5f53]"><span className="font-semibold">Autre :</span> {row.other}</p>}
            {row.imageIds?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {row.imageIds.map((id) => <FeedbackImage key={id} id={id} token={token} />)}
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
};

const TABS = [
  ["overview", "Vue d’ensemble"],
  ["users", "Utilisateurs"],
  ["waitlist", "Liste d’attente"],
  ["journal", "Journal"],
  ["feedback", "Commentaires"],
];

const AdminConsole = ({ session }) => {
  const [tab, setTab] = useState("users");
  useEffect(() => {
    const previous = document.title;
    document.title = "Admin · Plan Étude";
    return () => {
      document.title = previous;
    };
  }, []);
  return (
    <div className="mx-auto w-full max-w-[90rem] px-4 pb-16 pt-24 sm:px-6 lg:px-8" data-testid="admin-console">
      <h1 className="font-display text-4xl font-bold tracking-tight text-[#122118] sm:text-5xl">Console admin</h1>
      <p className="mt-3 text-[#4a5f53]">Tableau de bord interne — {session.email}</p>
      <div className="mt-6 flex flex-wrap gap-2" role="tablist">
        {TABS.map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            data-testid={`admin-tab-${id}`}
            onClick={() => setTab(id)}
            className={`rounded-full border px-5 py-2.5 text-sm font-semibold transition-colors ${
              tab === id
                ? "border-[#25D366] bg-[#25D366] text-[#0a2914]"
                : "border-gray-200 bg-white text-[#2d5a3d] hover:border-[#25D366]"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="mt-6">
        {tab === "overview" && <Overview token={session.token} />}
        {tab === "users" && <Users token={session.token} selfId={session.id} />}
        {tab === "waitlist" && <Waitlist token={session.token} />}
        {tab === "journal" && <Journal token={session.token} />}
        {tab === "feedback" && <Feedback token={session.token} />}
      </div>
    </div>
  );
};

export default AdminConsole;
